import type { Locale } from './types';

const SUPPORTED_LOCALES: Locale[] = ['tr-TR', 'en-US', 'kk-KZ', 'az-AZ'];
const DEFAULT_LOCALE: Locale = 'en-US';

const SHORT_LOCALE_MAP: Record<string, Locale> = {
  tr: 'tr-TR',
  en: 'en-US',
  kk: 'kk-KZ',
  az: 'az-AZ',
};

/**
 * Get locale from URL parameter (?lang=tr-TR or ?lang=tr or ?language=tr)
 */
export function getLocaleFromURL(): Locale | null {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  const lang = params.get('lang') || params.get('language');

  if (!lang) return null;

  const lower = lang.toLowerCase();

  if (SUPPORTED_LOCALES.includes(lang as Locale)) {
    return lang as Locale;
  }

  if (SHORT_LOCALE_MAP[lower]) {
    return SHORT_LOCALE_MAP[lower];
  }

  return null;
}

/**
 * Get locale from browser language setting
 */
export function getBrowserLocale(): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;

  const browserLang = navigator.language;

  // Check if Turkish
  if (browserLang.startsWith('tr')) {
    return 'tr-TR';
  }

  if (browserLang.startsWith('kk')) {
    return 'kk-KZ';
  }

  if (browserLang.startsWith('az')) {
    return 'az-AZ';
  }

  return 'en-US';
}

/**
 * Get locale with priority:
 * 1. URL parameter (?lang=)
 * 2. Browser language
 * 3. Default (en-US)
 */
export function getLocale(): Locale {
  return getLocaleFromURL() ?? getBrowserLocale();
}

/**
 * Build URL with lang parameter preserved
 */
export function buildUrlWithLocale(path: string, locale?: Locale): string {
  if (!locale) return path;

  const url = new URL(path, window.location.origin);
  url.searchParams.set('lang', locale);
  return url.pathname + url.search;
}
